import React, { useCallback, useEffect, useState } from "react";
import {
  Landmark, CheckCircle2, XCircle, Clock, Loader2, ShieldAlert, History,
  FileText, CreditCard, IndianRupee, Building2, AlertTriangle,
} from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import {
  fetchFinancialChanges, approveFinancialChange, rejectFinancialChange,
  notifyApprovalsChanged,
} from "@admin/services/adminApi";
import { adminKycDocUrl } from "@lawyer/services/kycApi";

const STATUS_TABS = [
  { key: "pending_approval", label: "Pending approval", icon: Clock },
  { key: "approved", label: "Approved", icon: CheckCircle2 },
  { key: "rejected", label: "Rejected", icon: XCircle },
  { key: "all", label: "All history", icon: History },
];

const STATUS_PILL = {
  pending_otp: { cls: "bg-amber-100 text-amber-800", label: "Awaiting OTP" },
  pending_approval: { cls: "bg-sky-100 text-sky-800", label: "Pending approval" },
  approved: { cls: "bg-emerald-100 text-emerald-800", label: "Approved" },
  rejected: { cls: "bg-rose-100 text-rose-800", label: "Rejected" },
  cancelled: { cls: "bg-slate-100 text-slate-500", label: "Cancelled" },
  expired: { cls: "bg-slate-100 text-slate-500", label: "Expired" },
};

const FIELD_META = {
  bank_account: { label: "Bank account", icon: Building2, cls: "bg-indigo-100 text-indigo-800" },
  upi_id: { label: "UPI ID", icon: IndianRupee, cls: "bg-emerald-100 text-emerald-800" },
  pan: { label: "PAN", icon: CreditCard, cls: "bg-amber-100 text-amber-800" },
  gst: { label: "GST", icon: FileText, cls: "bg-fuchsia-100 text-fuchsia-800" },
};

function Pill({ status }) {
  const p = STATUS_PILL[status] || { cls: "bg-slate-100 text-slate-700", label: status };
  return <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-bold uppercase tracking-wider ${p.cls}`}>{p.label}</span>;
}

function FieldChip({ field }) {
  const meta = FIELD_META[field] || { label: field, icon: Landmark, cls: "bg-slate-100 text-slate-700" };
  const Icon = meta.icon;
  return (
    <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-bold uppercase tracking-wider ${meta.cls}`}>
      <Icon size={10} /> {meta.label}
    </span>
  );
}

function DecisionModal({ open, item, action, onClose, onSubmit, busy }) {
  const [note, setNote] = useState("");
  useEffect(() => { if (!open) setNote(""); }, [open]);
  if (!open || !item) return null;
  const isReject = action === "reject";
  return (
    <div className="fixed inset-0 z-50 bg-black/50 grid place-items-center p-4" data-testid="fin-decision-modal">
      <div className="max-w-md w-full bg-white rounded-2xl p-5 shadow-xl">
        <h3 className="text-lg font-extrabold text-[#0A2540]" style={{ fontFamily: "Poppins" }}>
          {isReject ? "Reject change request" : "Approve change request"}
        </h3>
        <p className="mt-1 text-xs text-slate-500">
          {isReject
            ? "Rejection is final. The lawyer will need to open a new request. Provide a clear reason so they know what to fix."
            : "Approval applies the change immediately to the lawyer's payout profile and notifies them via email and SMS."}
        </p>
        <div className="mt-3 text-xs text-slate-600">
          <p>Changing <strong>{FIELD_META[item.field]?.label || item.field}</strong>:</p>
          <p className="mt-1 text-slate-500">
            <span className="line-through">{item.old_value_masked || "—"}</span>
            <span className="mx-2 text-slate-300">→</span>
            <strong>{item.new_value_masked}</strong>
          </p>
        </div>
        <Textarea
          value={note}
          onChange={(e) => setNote(e.target.value)}
          placeholder={isReject ? "Reason (required)" : "Approver note (optional)"}
          rows={3}
          data-testid="fin-note-input"
          className="mt-3 rounded-xl"
        />
        <div className="mt-4 flex justify-end gap-2">
          <Button variant="outline" onClick={onClose} data-testid="fin-cancel">Cancel</Button>
          <Button
            onClick={() => onSubmit(note.trim())}
            disabled={busy || (isReject && !note.trim())}
            data-testid="fin-confirm"
            className={isReject ? "bg-rose-600 hover:bg-rose-700 text-white rounded-xl" : "adv-btn-primary rounded-xl"}
          >
            {busy ? <Loader2 size={14} className="animate-spin mr-1" /> : null}
            {isReject ? "Reject" : "Approve"}
          </Button>
        </div>
      </div>
    </div>
  );
}

const SOURCE_LABEL = { digilocker_pan: "DigiLocker PAN", digilocker_aadhaar: "DigiLocker Aadhaar", registered_name: "registered name" };

// Bank account holder name vs the name as per PAN (DigiLocker-issued when available). Warning only.
function HolderNameCheck({ id, check }) {
  if (!check) return null;
  if (check.status === "matched") {
    return (
      <p className="mt-1 text-[11px] text-emerald-700" data-testid={`fin-holder-matched-${id}`}>
        Holder name matches {SOURCE_LABEL[check.compared_to_source] || check.compared_to_source} ({check.compared_to_name})
      </p>
    );
  }
  if (check.status === "unavailable") return null;
  return (
    <div className="mt-2 rounded-xl border border-amber-300 bg-amber-50 text-amber-900 px-3 py-2 text-xs" role="alert" data-testid={`fin-holder-mismatch-${id}`}>
      <p className="font-bold inline-flex items-center gap-1"><AlertTriangle size={12} /> Holder name mismatch</p>
      <p className="mt-1">
        Account holder: <strong data-testid={`fin-holder-name-${id}`}>{check.holder_name}</strong>
        <span className="mx-2 text-amber-400">|</span>
        {SOURCE_LABEL[check.compared_to_source] || "Reference"}: <strong data-testid={`fin-holder-ref-${id}`}>{check.compared_to_name || "—"}</strong>
      </p>
      <p className="mt-1 text-amber-800/90">The holder name must be as per PAN. Please verify before approving.</p>
    </div>
  );
}

function RequestRow({ item, onApprove, onReject }) {
  const canDecide = item.status === "pending_approval";
  return (
    <div className="border border-slate-200 rounded-2xl p-4 bg-white" data-testid={`fin-row-${item.id}`}>
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div className="min-w-0">
          <div className="flex items-center gap-2 mb-1">
            <Pill status={item.status} />
            <FieldChip field={item.field} />
            <span className="text-[10px] text-slate-400">{item.created_at?.slice(0, 19).replace("T", " ")}</span>
          </div>
          <p className="text-sm text-[#0A2540]">
            <span className="line-through text-slate-400">{item.old_value_masked || "—"}</span>
            <span className="mx-2 text-slate-300">→</span>
            <strong>{item.new_value_masked}</strong>
          </p>
          <div className="mt-1 text-[11px] text-slate-500">
            subject: {item.subject_id?.slice(0, 12)} · {item.subject_email || item.subject_phone}
          </div>
          {item.reason && (
            <p className="mt-1 text-xs text-slate-600 italic">&ldquo;{item.reason}&rdquo;</p>
          )}
          {item.attachment_ref && (
            <button
              type="button" data-testid={`fin-attachment-${item.id}`}
              className="mt-1 text-[11px] text-blue-700 underline inline-flex items-center gap-1 disabled:text-slate-400 disabled:no-underline"
              disabled={!item.attachment}
              title={item.attachment ? item.attachment.filename : "File no longer available"}
              onClick={async () => {
                try {
                  const r = await adminKycDocUrl(item.attachment.lawyer_id, item.attachment.doc_id);
                  const url = r.url?.startsWith("http") ? r.url : `${process.env.REACT_APP_BACKEND_URL}${r.url}`;
                  window.open(url, "_blank", "noopener,noreferrer");
                } catch (e) { toast.error(e?.response?.data?.detail || "Could not open attachment"); }
              }}
            >
              <FileText size={11} /> Attached document{item.attachment?.filename ? ` · ${item.attachment.filename}` : ""}
            </button>
          )}
          {item.approver_notes && (
            <p className="mt-1 text-xs text-slate-600">
              <strong>Approver note:</strong> {item.approver_notes}
            </p>
          )}
          {item.verification_status && (
            <p className="mt-1 text-[11px]">
              <span className="text-slate-500">external verification: </span>
              <span
                className={`inline-flex items-center gap-1 px-1.5 py-0.5 rounded-full text-[10px] font-bold uppercase ${
                  item.verification_status === "sandbox_verified"
                    ? "bg-emerald-100 text-emerald-800"
                    : item.verification_status === "sandbox_name_mismatch"
                    ? "bg-amber-100 text-amber-800"
                    : item.verification_status === "sandbox_invalid"
                    ? "bg-rose-100 text-rose-800"
                    : "bg-slate-100 text-slate-600"
                }`}
              >
                {item.verification_status === "sandbox_verified" ? "NSDL verified" : item.verification_status.replace(/_/g, " ")}
              </span>
              {item.verification_details?.name_on_pan && (
                <span className="ml-2 text-slate-500">NSDL name: <strong className="text-[#0A2540]">{item.verification_details.name_on_pan}</strong></span>
              )}
            </p>
          )}
          <HolderNameCheck id={item.id} check={item.verification_details?.holder_name_match} />
        </div>
        <div className="flex items-center gap-2">
          {canDecide ? (
            <>
              <Button size="sm" variant="outline" onClick={() => onReject(item)} data-testid={`fin-reject-${item.id}`}>
                <XCircle size={14} className="mr-1" /> Reject
              </Button>
              <Button size="sm" onClick={() => onApprove(item)} className="adv-btn-primary" data-testid={`fin-approve-${item.id}`}>
                <CheckCircle2 size={14} className="mr-1" /> Approve
              </Button>
            </>
          ) : (
            <span className="text-[11px] text-slate-400">
              {item.applied_at ? `applied ${item.applied_at.slice(0, 10)}` : ""}
              {item.rejected_at ? `rejected ${item.rejected_at.slice(0, 10)}` : ""}
            </span>
          )}
        </div>
      </div>
    </div>
  );
}

export default function AdminFinancialChanges() {
  const [tab, setTab] = useState("pending_approval");
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [busy, setBusy] = useState(false);
  const [decision, setDecision] = useState(null);

  const reload = useCallback(async () => {
    setLoading(true);
    try {
      const params = { limit: 100 };
      if (tab !== "all") params.status = tab;
      const d = await fetchFinancialChanges(params);
      setItems(d.items || []);
      setTotal(d.total || 0);
      notifyApprovalsChanged();
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not load requests"); }
    finally { setLoading(false); }
  }, [tab]);

  useEffect(() => { reload(); }, [reload]);

  const submitDecision = async (note) => {
    if (!decision) return;
    setBusy(true);
    try {
      if (decision.action === "approve") await approveFinancialChange(decision.item.id, note);
      else await rejectFinancialChange(decision.item.id, note);
      toast.success(decision.action === "approve" ? "Change applied" : "Request rejected");
      setDecision(null);
      reload();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
    finally { setBusy(false); }
  };

  return (
    <>
      <div className="mb-6">
        <h1 className="text-2xl sm:text-3xl font-extrabold text-[#0A2540]" style={{ fontFamily: "Poppins" }}>
          <Landmark size={20} className="inline mr-2 text-[#D4AF37]" />
          KYC / Payout Changes
        </h1>
        <p className="mt-1 text-sm text-slate-500">
          Review lawyer requests to update bank account, UPI, PAN or GST.
          Approving updates the lawyer&apos;s payout profile and notifies them on both
          contact channels.
        </p>
      </div>

      <div className="flex flex-wrap items-center gap-2 mb-4">
        {STATUS_TABS.map((t) => {
          const Icon = t.icon;
          return (
            <button
              key={t.key}
              onClick={() => setTab(t.key)}
              data-testid={`fin-tab-${t.key}`}
              className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-bold border transition ${
                tab === t.key ? "bg-[#0A2540] text-[#D4AF37] border-[#0A2540]" : "border-slate-200 text-slate-600 hover:border-[#0A2540]"
              }`}
            >
              <Icon size={12} /> {t.label}
              {t.key === "pending_approval" && total > 0 && tab === t.key && (
                <span className="ml-1 inline-flex items-center justify-center min-w-[18px] h-4 px-1 rounded-full text-[10px] font-bold bg-rose-500 text-white">
                  {total}
                </span>
              )}
            </button>
          );
        })}
        <div className="ml-auto text-[11px] text-slate-400 uppercase tracking-widest font-bold">
          Showing {items.length} of {total}
        </div>
      </div>

      {loading && <p className="text-xs text-slate-400"><Loader2 size={12} className="animate-spin inline mr-1" /> Loading…</p>}
      {!loading && items.length === 0 && (
        <div className="text-center bg-slate-50 border border-slate-200 rounded-2xl p-10" data-testid="fin-empty">
          <ShieldAlert size={28} className="mx-auto text-slate-400 mb-2" />
          <p className="text-sm text-slate-500">
            {tab === "pending_approval" ? "No pending KYC changes — you’re all clear." : "No requests match this filter."}
          </p>
        </div>
      )}
      <div className="space-y-3">
        {items.map((item) => (
          <RequestRow
            key={item.id}
            item={item}
            onApprove={(x) => setDecision({ action: "approve", item: x })}
            onReject={(x) => setDecision({ action: "reject", item: x })}
          />
        ))}
      </div>

      <DecisionModal
        open={!!decision}
        item={decision?.item}
        action={decision?.action}
        onClose={() => setDecision(null)}
        onSubmit={submitDecision}
        busy={busy}
      />
    </>
  );
}
